import { Component } from "react";
import PropTypes from "prop-types";
import flatpickr from "flatpickr";

const hooks = [
  "onChange",
  "onOpen",
  "onClose",
  "onMonthChange",
  "onYearChange",
  "onReady",
  "onValueUpdate",
  "onDayCreate",
];
const hookPropType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.arrayOf(PropTypes.func),
]);

const callbacks = ["onCreate", "onDestroy"];

const callbackPropTypes = PropTypes.func;

export class Flatpickr extends Component {
  static propTypes = {
    defaultValue: PropTypes.string,
    options: PropTypes.object,
    onChange: hookPropType,
    onOpen: hookPropType,
    onClose: hookPropType,
    onMonthChange: hookPropType,
    onYearChange: hookPropType,
    onReady: hookPropType,
    onValueUpdate: hookPropType,
    onDayCreate: hookPropType,
    onCreate: callbackPropTypes,
    onDestroy: callbackPropTypes,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
    ]),
    children: PropTypes.node,
    className: PropTypes.string,
    render: PropTypes.func,
  };

  static defaultProps = {
    options: {},
  };

  componentDidUpdate(prevProps) {
    let { options } = this.props;
    let prevOptions = prevProps.options;

    options = mergeHooks(options, this.props);

    // Add prev ones too so we can compare against them later
    prevOptions = mergeHooks(prevOptions, prevProps);

    const optionsKeys = Object.getOwnPropertyNames(options);
    for (let index = optionsKeys.length - 1; index >= 0; index--) {
      const key = optionsKeys[index];
      let value = options[key];

      // Skip setting hooks during update to prevent resetting flatpickr internal event handlers mid-click
      if (hooks.indexOf(key) !== -1) {
        continue;
      }

      if (value !== prevOptions[key]) {
        this.flatpickr.set(key, value);
      }
    }

    if (Object.prototype.hasOwnProperty.call(this.props, "value")) {
      const isValueArray = Array.isArray(this.props.value);
      const isPrevValueArray = Array.isArray(prevProps.value);
      
      let isSame = false;
      if (isValueArray && isPrevValueArray && this.props.value.length === prevProps.value.length) {
        isSame = this.props.value.every((v, i) => {
          const prev = prevProps.value[i];
          if (v instanceof Date && prev instanceof Date) {
            return v.getTime() === prev.getTime();
          }
          return v === prev;
        });
      } else if (!isValueArray && !isPrevValueArray) {
        if (this.props.value instanceof Date && prevProps.value instanceof Date) {
          isSame = this.props.value.getTime() === prevProps.value.getTime();
        } else {
          isSame = this.props.value === prevProps.value;
        }
      }

      // Check if flatpickr instance is already in sync with this.props.value
      let isAlreadyInSync = false;
      if (this.flatpickr) {
        const inputVal = this.flatpickr.input?.value;
        const altInputVal = this.flatpickr.altInput?.value;
        const selected = this.flatpickr.selectedDates || [];

        if (typeof this.props.value === "string") {
          const valTrimmed = this.props.value.trim();
          if (valTrimmed && (valTrimmed === (inputVal || "").trim() || valTrimmed === (altInputVal || "").trim())) {
            isAlreadyInSync = true;
          }
        }

        if (!isAlreadyInSync && selected.length > 0) {
          let propDates = [];
          if (this.props.value instanceof Date) {
            propDates = [this.props.value];
          } else if (typeof this.props.value === "string" && this.props.value) {
            const parsed = this.flatpickr.parseDate ? this.flatpickr.parseDate(this.props.value) : null;
            if (parsed && !isNaN(parsed.getTime())) propDates = [parsed];
          } else if (Array.isArray(this.props.value)) {
            propDates = this.props.value
              .map((v) =>
                v instanceof Date
                  ? v
                  : typeof v === "string" && this.flatpickr.parseDate
                  ? this.flatpickr.parseDate(v)
                  : null
              )
              .filter((d) => d && !isNaN(d.getTime()));
          }

          if (
            propDates.length > 0 &&
            propDates.length === selected.length &&
            propDates.every((d, i) => d && selected[i] && d.getTime() === selected[i].getTime())
          ) {
            isAlreadyInSync = true;
          }
        }
      }

      if (!isSame && !isAlreadyInSync) {
        const isInputFocused =
          this.flatpickr &&
          (document.activeElement === this.flatpickr.input ||
            document.activeElement === this.flatpickr.altInput);

        if (!isInputFocused || !this.props.value) {
          if (!this.props.value) {
            this.flatpickr.clear(false);
          } else {
            this.flatpickr.setDate(this.props.value, false);
          }
        }
      }
    }
  }

  componentDidMount() {
    this.createFlatpickrInstance();
  }

  componentWillUnmount() {
    this.destroyFlatpickrInstance();
  }

  createFlatpickrInstance = () => {
    let options = {
      onClose: () => {
        this.node.blur && this.node.blur();
      },
      ...this.props.options,
    };

    // Add prop hooks to options
    options = mergeHooks(options, this.props);

    this.flatpickr = flatpickr(this.node, options);

    if (Object.prototype.hasOwnProperty.call(this.props, "value")) {
      this.flatpickr.setDate(this.props.value, false);
    }

    const { onCreate } = this.props;
    if (onCreate) onCreate(this.flatpickr);
  };

  destroyFlatpickrInstance = () => {
    const { onDestroy } = this.props;
    if (onDestroy) onDestroy(this.flatpickr);
    if (this.flatpickr) {
      this.flatpickr.destroy();
    }
    this.flatpickr = null;
  };

  handleNodeChange = (node) => {
    this.node = node;
    if (this.flatpickr) {
      this.destroyFlatpickrInstance();
      this.createFlatpickrInstance();
    }
  };

  render() {
    const { options, defaultValue, value, children, render, ...props } =
      this.props;

    // Don't pass hooks and callbacks to dom node
    hooks.forEach((hook) => {
      delete props[hook];
    });
    callbacks.forEach((callback) => {
      delete props[callback];
    });

    if (render)
      return render({ ...props, defaultValue, value }, this.handleNodeChange);

    return options.wrap ? (
      <div {...props} ref={this.handleNodeChange}>
        {children}
      </div>
    ) : (
      <input
        {...props}
        defaultValue={defaultValue}
        ref={this.handleNodeChange}
      />
    );
  }
}

function mergeHooks(inputOptions, props) {
  const options = { ...inputOptions };

  hooks.forEach((hook) => {
    if (Object.prototype.hasOwnProperty.call(props, hook)) {
      if (options[hook] && !Array.isArray(options[hook])) {
        options[hook] = [options[hook]];
      } else if (!options[hook]) {
        options[hook] = [];
      }

      const propHook = Array.isArray(props[hook]) ? props[hook] : [props[hook]];
      options[hook].push(...propHook);
    }
  });

  return options;
}